import { Upload, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { FileUploadProgress } from "components/upload/FileUploadProgress";
import { useGeometryUpload } from "components/boundary";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { BoundaryToolProps } from "./define-boundary";

export const UploadComponent = (
  props: ClipConfigProps & ClipStepsControlProps & BoundaryToolProps
) => {
  const {
    uploadState,
    featureExtractCount,
    warnings,
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    getRootProps,
    getInputProps,
    resetUpload,
    cancelUpload,
  } = useGeometryUpload({
    draw: props.draw,
    addOrUpdateFeatures: props.addOrUpdateFeatures,
    mapRef: props.mapRef as React.RefObject<any>,
  });

  return (
    <div>
      {uploadState === "ready" && (
        <div
          {...getRootProps()}
          className="border-2 border-dashed border-muted-foreground/40 m-2 p-2 text-center cursor-pointer rounded-lg dropzone"
        >
          <input disabled={true} {...getInputProps({ multiple: false })} />
          <Upload className="size-5 text-info mx-auto mb-1" />
          <p className="text-sm">Click to upload or drag and drop file</p>
          <p className="text-xs text-muted-foreground">*Max file size is 10 MB</p>
        </div>
      )}

      {uploadState === "uploading" && (
        <FileUploadProgress
          uploadProgress={uploadProgress}
          file={files[0]}
          onRemove={cancelUpload}
          isUploading={isUploading}
          haveUploaded={haveUploaded}
        />
      )}
      {uploadState === "processing" && (
        <div className="flex flex-col justify-center items-center mt-4">
          <h6 className="text-base font-medium">Processing file</h6>
          <Spinner className="mt-4" />
        </div>
      )}
      {uploadState === "complete" &&
        warnings.length > 0 &&
        warnings.map((x, i) => (
          <Alert key={i} className="mt-4">
            <AlertTriangle className="size-4" />
            <AlertDescription>{x}</AlertDescription>
          </Alert>
        ))}
      {uploadState === "complete" &&
        (featureExtractCount > 0 ? (
          <Alert className="mt-4">
            <AlertDescription>
              {featureExtractCount} feature(s) extracted successfully
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="mt-4" variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>No features could be extracted</AlertDescription>
          </Alert>
        ))}
      {uploadState === "complete" && (
        <Button
          variant="outline"
          className="mt-4"
          onClick={resetUpload}
        >
          Upload again
        </Button>
      )}
    </div>
  );
};
